import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  PersonalAccountOpenProfileDetailsPages,
  usePersonalAccountOpenProfileDetailsPageTypeStore,
} from 'views/pages/accountOpenProfileDetails/personalProfileDetails/hooks';
import { useEffect } from 'react';
import { usePersonalAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yapStringWhen, yupNumberString } from '../common';
import { useGetDateTimeQuery } from 'views/utils/graphql/graphql';
import {
  CorporateBusinessContent,
  FatcaStatus,
  Occupation,
  OccupationStrs,
  DomesticRealEstatePurpose,
  DomesticRealEstatePurposeStrs,
} from 'views/utils/enums';
import { AddressInfo } from 'views/templates/module/addressInput/hook';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { CheckBoxItemType } from 'views/templates/module/checkInput';
import { Direction } from 'views/mutual/inputRadio';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';

export enum CorporateAddressConfirmSelectType {
  None = 0,
  Same = 1,
  Different = 2,
}

export enum Gender {
  None = 0,
  Male,
  Female,
  PreferNotToSay,
}

export enum ImportantPublicWorkerType {
  None = 0,
  No,
  Yes,
}

export enum AuthModeSelectType {
  None = 0,
  SMS = 1,
  MailAddress = 2,
}

export type CustomerInfoType = {
  customerFirstName: string;
  customerLastName: string;
  customerFirstNameKana: string;
  customerLastNameKana: string;
  gender: Gender;
  customerPostCode: string;
  customerPrefecture: string;
  customerCityCountry: string;
  customerTownStreetAddress: string;
  hasNoCustomerTownStreetAddress: boolean; // リクエストに不要
  customerBuildingNameAndRoomNumber: string;
  customerMobilePhone: string;
  customerHomePhone: string;
  customerFaxNumber: string;
  customerMailAddress: string;
  customerConfirmMailAddress: string;
  occupation: CheckBoxItemType[];
  occupationOtherText: string;
  customerMainBusinessContent: string;
  customerMainBusinessContentOtherText: string;
  customerOtherBusinessContent: string;
  customerOtherBusinessContentOtherText: string;
  purpose: CheckBoxItemType[];
  purposeOtherText: string;
  fatca: FatcaStatus;
  importantPublicWorker: ImportantPublicWorkerType;
  bankName: string;
  branchName: string;
  accountType: AccountType;
  accountNumber: string;
  accountHolderName: string;
  invoiceNumber: string;
  hasNoInvoiceNumber: boolean;
  authMode: AuthModeSelectType;
  electronicDocumentAgreement: boolean;
  electronicDocumentAgreementDateTime: string;
};

export const useCustomerInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = usePersonalAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = usePersonalAccountOpenProfileDetailsPageTypeStore();
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const checkLabelArraySchema = Yup.object().shape({
    label: Yup.string(),
    checked: Yup.boolean(),
  });

  const validation = Yup.object().shape({
    customerFirstName: Yup.string().required(ErrorWord.INPUT).max(80, getMaxLengthErrorMessage(80)),
    customerLastName: Yup.string().required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
    customerFirstNameKana: Yup.string()
      .required(ErrorWord.INPUT)
      .matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA)
      .max(80, getMaxLengthErrorMessage(80)),
    customerLastNameKana: Yup.string()
      .required(ErrorWord.INPUT)
      .matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA)
      .max(255, getMaxLengthErrorMessage(255)),
    gender: !isUpdate ? Yup.number().not([Gender.None], ErrorWord.SELECT) : Yup.number().nullable(),
    customerPostCode: yupNumberString({ length: 7, isRequired: true, errorWord: ErrorWord.POSTCODE }),
    customerPrefecture: Yup.string().required(ErrorWord.SELECT),
    customerCityCountry: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    customerTownStreetAddress: yapStringWhen('hasNoCustomerTownStreetAddress', false, ErrorWord.INPUT).max(
      255,
      getMaxLengthErrorMessage(255),
    ),
    customerBuildingNameAndRoomNumber: Yup.string().max(50, getMaxLengthErrorMessage(50)),
    customerMobilePhone: yupNumberString({ length: 11, isRequired: true, errorWord: ErrorWord.MOBILE_PHONE }),
    customerHomePhone: yupNumberString({ length: 10, isRequired: false, errorWord: ErrorWord.HOME_PHONE }),
    customerFaxNumber: yupNumberString({ length: 10, isRequired: false, errorWord: ErrorWord.HOME_PHONE }),
    customerMailAddress: Yup.string()
      .trim()
      .required(ErrorWord.EMAIL)
      .email(ErrorWord.EMAIL)
      .max(80, getMaxLengthErrorMessage(80)),
    customerConfirmMailAddress: Yup.string()
      .required(ErrorWord.EMAIL_CONFIRM)
      .oneOf([Yup.ref('customerMailAddress')], ErrorWord.EMAIL_CONFIRM),
    occupation: Yup.array()
      .of(checkLabelArraySchema)
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, (list) => list?.some((item) => item.checked)),
    occupationOtherText: yapStringWhen(
      'occupation',
      (val: CheckBoxItemType[]) => val.filter((x) => x.checked).some((x) => x.label === Occupation.Other),
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),

    customerMainBusinessContent: yapStringWhen(
      'occupation',
      (val: CheckBoxItemType[]) =>
        val
          .filter((x) => x.checked)
          .some((x) => x.label === Occupation.CompanyEmployee || x.label === Occupation.SelfEmployed),
      ErrorWord.SELECT,
    ),

    customerMainBusinessContentOtherText: yapStringWhen(
      'customerMainBusinessContent',
      (val: string) => val === CorporateBusinessContent.Sonohoka,
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    customerOtherBusinessContentOtherText: yapStringWhen(
      'customerOtherBusinessContent',
      (val: string) => val === CorporateBusinessContent.Sonohoka,
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),

    purpose: Yup.array()
      .of(checkLabelArraySchema)
      .test('ArrayCheck', ErrorWord.SELECT_ONE_OR_MORE, (list) => list?.some((item) => item.checked)),
    purposeOtherText: yapStringWhen(
      'purpose',
      (val: CheckBoxItemType[]) =>
        val.filter((x) => x.checked).some((x) => x.label === DomesticRealEstatePurpose.Other),
      ErrorWord.INPUT,
    ).max(25, getMaxLengthErrorMessage(25)),
    fatca: Yup.number()
      .not([FatcaStatus.None], ErrorWord.SELECT)
      .is(
        [FatcaStatus.No],
        `恐れ入りますが、${
          !isUpdate ? '口座開設' : 'ご登録内容の変更'
        }はお受けできません。取引を希望される場合は最寄りの営業拠点にお問い合わせください。`,
      ),
    importantPublicWorker: Yup.number()
      .not([ImportantPublicWorkerType.None], ErrorWord.SELECT)
      .is(
        [ImportantPublicWorkerType.No],
        `恐れ入りますが、${
          !isUpdate ? '口座開設' : 'ご登録内容の変更'
        }はお受けできません。取引を希望される場合は最寄りの営業拠点にお問い合わせください。`,
      ),
    bankName: Yup.string().required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
    branchName: Yup.string().required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
    accountType: Yup.number().not([AccountType.None], ErrorWord.SELECT),
    accountNumber: yupNumberString({ length: 7, isRequired: true, errorWord: ErrorWord.BANKNUMBER }),
    accountHolderName: Yup.string().required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
    invoiceNumber: Yup.string().when('hasNoInvoiceNumber', {
      is: false,
      then: () =>
        yupNumberString({
          length: 13,
          isRequired: true,
          errorWord: ErrorWord.INVOICE_NUMBER,
        }),
      otherwise: (schema) => schema.strip(),
    }),
    authMode: Yup.number().not([AuthModeSelectType.None], ErrorWord.SELECT),
    electronicDocumentAgreement: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT),
  });

  const initialValues: CustomerInfoType = {
    customerFirstName: param.customerFirstName ?? '',
    customerLastName: param.customerLastName ?? '',
    customerFirstNameKana: param.customerFirstNameKana ?? '',
    customerLastNameKana: param.customerLastNameKana ?? '',
    gender: param.gender ?? Gender.None,
    customerPostCode: param.customerPostCode ?? '',
    customerPrefecture: param.customerPrefecture ?? '',
    customerCityCountry: param.customerCityCountry ?? '',
    customerTownStreetAddress: param.customerTownStreetAddress ?? '',
    hasNoCustomerTownStreetAddress: param.hasNoCustomerTownStreetAddress ?? false,
    customerBuildingNameAndRoomNumber: param.customerBuildingNameAndRoomNumber ?? '',
    customerMobilePhone: param.customerMobilePhone ?? '',
    customerHomePhone: param.customerHomePhone ?? '',
    customerFaxNumber: param.customerFaxNumber ?? '',
    customerMailAddress: param.customerMailAddress ?? (isUpdate ? '' : param.mailAddressQuestion),
    customerConfirmMailAddress: param.customerConfirmMailAddress ?? (isUpdate ? '' : param.mailAddressQuestion),
    occupation: OccupationStrs.map<CheckBoxItemType>((data) => {
      return { label: data, checked: param.occupation?.find((x) => x.label === data)?.checked ?? false };
    }),
    occupationOtherText: param.occupationOtherText ?? '',
    customerMainBusinessContent: param.customerMainBusinessContent ?? '',
    customerMainBusinessContentOtherText: param.customerMainBusinessContentOtherText ?? '',
    customerOtherBusinessContent: param.customerOtherBusinessContent ?? '',
    customerOtherBusinessContentOtherText: param.customerOtherBusinessContentOtherText ?? '',
    purpose: DomesticRealEstatePurposeStrs.map<CheckBoxItemType>((data) => {
      return { label: data, checked: param.purpose?.find((x) => x.label === data)?.checked ?? false };
    }),
    purposeOtherText: param.purposeOtherText ?? '',
    fatca: param.fatca ?? FatcaStatus.None,
    importantPublicWorker: param.importantPublicWorker ?? ImportantPublicWorkerType.None,
    bankName: param.bankName ?? '',
    branchName: param.branchName ?? '',
    accountType: param.accountType ?? AccountType.None,
    accountNumber: param.accountNumber ?? '',
    accountHolderName: param.accountHolderName ?? '',
    invoiceNumber: param.invoiceNumber ?? '',
    hasNoInvoiceNumber: param.hasNoInvoiceNumber ?? false,
    electronicDocumentAgreement: param.electronicDocumentAgreement ?? false,
    electronicDocumentAgreementDateTime: param.electronicDocumentAgreementDateTime ?? '',
    authMode: param.authMode ?? AuthModeSelectType.None,
  };

  const formik = useFormik<CustomerInfoType>({
    enableReinitialize: true,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(PersonalAccountOpenProfileDetailsPages.entryConfirm);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  useEffect(() => {
    if (formik.values.electronicDocumentAgreement) {
      getDataTime();
    }
  }, [formik.values.electronicDocumentAgreement]);

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.electronicDocumentAgreementDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  const customerFirstNameProps = {
    value: formik.values.customerFirstName,
    onChange: (value: string) => formik.setFieldValue('customerFirstName', value),
    error: formik.errors.customerFirstName,
  };
  const customerLastNameProps = {
    value: formik.values.customerLastName,
    onChange: (value: string) => formik.setFieldValue('customerLastName', value),
    error: formik.errors.customerLastName,
  };
  const customerFirstNameKanaProps = {
    value: formik.values.customerFirstNameKana,
    onChange: (value: string) => formik.setFieldValue('customerFirstNameKana', value),
    error: formik.errors.customerFirstNameKana,
  };
  const customerLastNameKanaProps = {
    value: formik.values.customerLastNameKana,
    onChange: (value: string) => formik.setFieldValue('customerLastNameKana', value),
    error: formik.errors.customerLastNameKana,
  };
  const genderProps = {
    selected: formik.values.gender,
    onChange: (value: Gender) => {
      formik.setFieldValue('gender', value);
    },
    error: formik.errors.gender,
    direction: Direction.row,
  };
  const customerAddressProps = {
    postCode: {
      value: formik.values.customerPostCode,
      onChange: (value: string) => {
        formik.setFieldValue('customerPostCode', value);
      },
      error: formik.errors.customerPostCode,
    },
    prefecture: {
      selectedOption: formik.values.customerPrefecture,
      setSelectedOption: (value: string) => formik.setFieldValue('customerPrefecture', value),
      error: formik.errors.customerPrefecture,
    },
    municipality: {
      value: formik.values.customerCityCountry,
      onChange: (value: string) => {
        formik.setFieldValue('customerCityCountry', value);
      },
      error: formik.errors.customerCityCountry,
    },
    streetAddress: {
      value: formik.values.customerTownStreetAddress,
      onChange: (value: string) => {
        formik.setFieldValue('customerTownStreetAddress', value);
      },
      error: formik.errors.customerTownStreetAddress,
      disabled: formik.values.hasNoCustomerTownStreetAddress,
    },
    hasNoTownStreetAddress: {
      checked: formik.values.hasNoCustomerTownStreetAddress,
      setChecked: (value: boolean) => {
        formik.setFieldValue('customerTownStreetAddress', '');
        formik.setFieldValue('hasNoCustomerTownStreetAddress', value);
      },
    },
    buildingNameAndRoomNumber: {
      value: formik.values.customerBuildingNameAndRoomNumber,
      onChange: (value: string) => formik.setFieldValue('customerBuildingNameAndRoomNumber', value),
      error: formik.errors.customerBuildingNameAndRoomNumber,
    },
    onSearchAddress: (addressInfo: AddressInfo) => {
      formik.setFieldValue('customerPrefecture', addressInfo.prefecture);
      formik.setFieldValue('customerCityCountry', addressInfo.cityCountry);
      formik.setFieldValue('hasNoCustomerTownStreetAddress', false);
      formik.setFieldValue('customerTownStreetAddress', addressInfo.townStreetAddress);
      formik.setFieldValue('customerBuildingNameAndRoomNumber', '');
    },
  };
  const customerMobilePhoneProps = {
    value: formik.values.customerMobilePhone,
    onChange: (value: string) => formik.setFieldValue('customerMobilePhone', value),
    error: formik.errors.customerMobilePhone,
  };
  const customerHomePhoneProps = {
    value: formik.values.customerHomePhone,
    onChange: (value: string) => formik.setFieldValue('customerHomePhone', value),
    error: formik.errors.customerHomePhone,
  };
  const customerFaxNumberProps = {
    value: formik.values.customerFaxNumber,
    onChange: (value: string) => formik.setFieldValue('customerFaxNumber', value),
    error: formik.errors.customerFaxNumber,
  };
  const customerMailAddressProps = {
    value: formik.values.customerMailAddress,
    disabled: !isUpdate,
    onChange: (value: string) => formik.setFieldValue('customerMailAddress', value),
    error: formik.errors.customerMailAddress,
  };
  const customerConfirmMailAddressProps = {
    value: !isUpdate ? param.mailAddressQuestion : formik.values.customerConfirmMailAddress,
    disable: !isUpdate,
    onChange: (value: string) => formik.setFieldValue('customerConfirmMailAddress', value),
    error: formik.errors.customerConfirmMailAddress,
  };
  const occupationProps = {
    items: formik.values.occupation,
    name: 'occupation',
    onChangeItems: (items: CheckBoxItemType[]) => {
      if (!items.some((x) => x.checked && x.label === Occupation.Other)) {
        formik.setFieldValue('occupationOtherText', '');
      }
      if (
        !items.some((x) => x.checked && (x.label === Occupation.CompanyEmployee || x.label === Occupation.SelfEmployed))
      ) {
        formik.setFieldValue('customerMainBusinessContent', '');
        formik.setFieldValue('customerMainBusinessContentOtherText', '');
        formik.setFieldValue('customerOtherBusinessContent', '');
        formik.setFieldValue('customerOtherBusinessContentOtherText', '');
      }
      formik.setFieldValue('occupation', items);
    },
    error: formik.errors.occupation as string,
    otherText: formik.values.occupation.filter((x) => x.checked).some((x) => x.label === Occupation.Other) && {
      value: formik.values.occupationOtherText,
      onChange: (value: string) => formik.setFieldValue('occupationOtherText', value),
      error: formik.errors.occupationOtherText,
    },
    direction: Direction.row,
  };
  const customerMainBusinessContentProps = formik.values.occupation
    .filter((x) => x.checked)
    .some((x) => x.label === Occupation.CompanyEmployee || x.label === Occupation.SelfEmployed) && {
    selectedOption: formik.values.customerMainBusinessContent,
    setSelectedOption: (value: string) => {
      if (value !== CorporateBusinessContent.Sonohoka) {
        formik.setFieldValue('customerMainBusinessContentOtherText', '');
      }
      formik.setFieldValue('customerMainBusinessContent', value);
    },
    error: formik.errors.customerMainBusinessContent,
    otherText: formik.values.customerMainBusinessContent === CorporateBusinessContent.Sonohoka && {
      value: formik.values.customerMainBusinessContentOtherText,
      onChange: (value: string) => formik.setFieldValue('customerMainBusinessContentOtherText', value),
      error: formik.errors.customerMainBusinessContentOtherText,
    },
  };
  const customerOtherBusinessContentProps = formik.values.occupation
    .filter((x) => x.checked)
    .some((x) => x.label === Occupation.CompanyEmployee || x.label === Occupation.SelfEmployed) && {
    selectedOption: formik.values.customerOtherBusinessContent,
    setSelectedOption: (value: string) => {
      if (value !== CorporateBusinessContent.Sonohoka) {
        formik.setFieldValue('customerOtherBusinessContentOtherText', '');
      }
      formik.setFieldValue('customerOtherBusinessContent', value);
    },
    error: formik.errors.customerOtherBusinessContent,
    otherText: formik.values.customerOtherBusinessContent === CorporateBusinessContent.Sonohoka && {
      value: formik.values.customerOtherBusinessContentOtherText,
      onChange: (value: string) => formik.setFieldValue('customerOtherBusinessContentOtherText', value),
      error: formik.errors.customerOtherBusinessContentOtherText,
    },
  };
  const purposeProps = {
    items: formik.values.purpose,
    name: 'purpose',
    onChangeItems: (items: CheckBoxItemType[]) => {
      if (!items.some((x) => x.checked && x.label === DomesticRealEstatePurpose.Other)) {
        formik.setFieldValue('purposeOtherText', '');
      }
      formik.setFieldValue('purpose', items);
    },
    error: formik.errors.purpose as string,
    otherText: formik.values.purpose
      .filter((x) => x.checked)
      .some((x) => x.label === DomesticRealEstatePurpose.Other) && {
      value: formik.values.purposeOtherText,
      onChange: (value: string) => formik.setFieldValue('purposeOtherText', value),
      error: formik.errors.purposeOtherText,
    },
  };
  const fatcaProps = {
    selected: formik.values.fatca,
    onChange: async (value: FatcaStatus) => {
      await formik.setFieldValue('fatca', value);
      formik.validateField('fatca');
    },
    error: formik.errors.fatca,
  };
  const importantPublicWorkerProps = {
    selected: formik.values.importantPublicWorker,
    onChange: async (value: ImportantPublicWorkerType) => {
      await formik.setFieldValue('importantPublicWorker', value);
      formik.validateField('importantPublicWorker');
    },
    error: formik.errors.importantPublicWorker,
  };

  const bankDetailsInfoProps = {
    selectBankProps: {
      selected: formik.values.bankName,
      onSelectedBank: (value: string) => {
        formik.setFieldValue('bankName', value);
      },
      error: formik.errors.bankName,
    },
    selectBranchProps: {
      selected: formik.values.branchName,
      onSelectedBranch: (value: string) => {
        formik.setFieldValue('branchName', value);
      },
      error: formik.errors.branchName,
    },
    accountTypeProps: {
      selected: formik.values.accountType,
      onChange: (value: string) => formik.setFieldValue('accountType', value),
      error: formik.errors.accountType,
    },
    accountNumberProps: {
      value: formik.values.accountNumber,
      onChange: (value: string) => formik.setFieldValue('accountNumber', value),
      error: formik.errors.accountNumber,
    },
    accountHolderNameProps: {
      value: formik.values.accountHolderName,
      onChange: (value: string) => formik.setFieldValue('accountHolderName', value),
      error: formik.errors.accountHolderName,
    },
  };
  const invoiceNumberProps = {
    textInput: {
      value: formik.values.invoiceNumber,
      onChange: (value: string) => formik.setFieldValue('invoiceNumber', value),
      disabled: formik.values.hasNoInvoiceNumber,
      error: formik.errors.invoiceNumber,
    },
    checkInput: {
      checked: formik.values.hasNoInvoiceNumber,
      setChecked: (value: boolean) => {
        formik.setFieldValue('hasNoInvoiceNumber', value);
        if (value) {
          formik.setFieldValue('invoiceNumber', '');
        }
      },
      error: formik.errors.hasNoInvoiceNumber,
    },
  };
  const electronicDocumentAgreementProps = {
    checked: formik.values.electronicDocumentAgreement,
    setChecked: (value: boolean) => formik.setFieldValue('electronicDocumentAgreement', value),
    error: formik.errors.electronicDocumentAgreement,
  };
  const authModeProps = {
    selected: formik.values.authMode,
    onChange: (value: AuthModeSelectType) => {
      formik.setFieldValue('authMode', value);
    },
    error: formik.errors.authMode,
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    customerFirstNameProps,
    customerLastNameProps,
    customerFirstNameKanaProps,
    customerLastNameKanaProps,
    genderProps,
    customerAddressProps,
    customerMobilePhoneProps,
    customerHomePhoneProps,
    customerFaxNumberProps,
    customerMailAddressProps,
    customerConfirmMailAddressProps,
    occupationProps,
    customerMainBusinessContentProps,
    customerOtherBusinessContentProps,
    purposeProps,
    fatcaProps,
    importantPublicWorkerProps,
    bankDetailsInfoProps,
    invoiceNumberProps,
    electronicDocumentAgreementProps,
    authModeProps,
  };
};
